import {useContext, useState} from 'react';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {css} from '@emotion/css';
import {AxiosError} from 'axios';
import {sizes} from 'patient-ping-remedy/packages/theme';
import {ToastType} from 'patient-ping-remedy/packages/toast';
import Button, {StyleType} from 'patient-ping-remedy/packages/button';
import Icon, {ICONS} from 'patient-ping-remedy/packages/icon';
import IconButton from 'patient-ping-remedy/packages/icon-button';
import ContextMenu from 'patient-ping-remedy/packages/context-menu';
import {
  CareJourneyAttachment,
  CareJourneyAttachmentType,
  CareJourneyDetailsResponse
} from '@/api/dto/care-journey';
import {CarecoApiContext} from '@/app-context/careco-api-context';
import {useAuthStore} from '@/store/auth_store';
import {useActiveRoster} from '@/store/roster_store';
import {useAlertStore} from '@/store/alert_store';
import Table from '@common/Table';
import DeleteModal from '@common/DeleteModal';
import AttachmentStatus from '@/components/pages/tasks/task_edit/attachments/AttachmentStatus';
import UploadAttachmentModal from '@/components/pages/tasks/task_edit/attachments/UploadAttachmentModal';
import {
  AttachmentActions,
  AttachmentTitle,
  ButtonWrapper,
  EmptyTable,
  TableWrapper
} from '@/components/pages/tasks/task_edit/attachments/TaskEditAttachments.styles';
import {GENERICERROR} from '@manual_encounters/constants/BannerMessages';
import {NEW_RESOURCE} from '@/helpers/search_params';
import PermissionsHelpers from '@/helpers/permissions_helpers';
import DisplayHelpers from '@/helpers/display_helpers';
import FileHelpers from '@/helpers/file_helpers';
import Helpers from '@/helpers/helpers';
import {Feature, FeatureNew, FeaturePermissionLevel} from "@/api/dto/permissions";
import {FeatureToggle} from "@/api/dto/dto";
import {useFeatureToggles} from "@/hooks/useFeatureToggles";

type Props = {
  careJourneyDetails: CareJourneyDetailsResponse;
}

const getFileName = (attachment: CareJourneyAttachment) => {
  if (attachment.type === CareJourneyAttachmentType.DMCS_CARE_NOTE) {
    return attachment.fileName  ? `${attachment.fileName}.pdf` : 'Care note.pdf';
  }

  if (attachment.type === CareJourneyAttachmentType.CARE_TEAM_REFERRAL) {
    return attachment.fileName ? `${attachment.fileName}.pdf` : 'Care team referral.pdf';
  }

  if (attachment.s3Key) {
    return attachment.s3Key.split('/').pop() ?? 'Unknown';
  }

  if (attachment.type === CareJourneyAttachmentType.OUTREACH_ATTEMPTS_SUMMARY) {
    return attachment.fileName ? `${attachment.fileName}.pdf` : 'Outreach attempts.pdf';
  }

  return "Unknown";
};

export default function TaskEditAttachments({ careJourneyDetails }: Props) {
  const { carecoApi } = useContext(CarecoApiContext);
  const { currentUser } = useAuthStore();
  const currentRoster = useActiveRoster();
  const { addAlert } = useAlertStore();
  const queryClient = useQueryClient();
  const {checkBooleanToggle} = useFeatureToggles();

  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [deleteModalAttachmentId, setDeleteModalAttachmentId] = useState<string | null>(null);

  const isNewPermissionsEnabled = checkBooleanToggle(FeatureToggle.CARECO_3415_NEW_USER_PERMISSIONS);

  const canActOnTasks = isNewPermissionsEnabled ?
    PermissionsHelpers.canForUserFeaturePermissionRosterNew(
      currentUser!,
      currentRoster!,
      FeaturePermissionLevel.ACT,
      FeatureNew.TASKS
    ) : PermissionsHelpers.canForUserFeaturePermissionRoster(
      currentUser!,
      currentRoster!,
      FeaturePermissionLevel.ACT,
      Feature.TASKS
    );

  const { mutate: handleDelete } = useMutation({
    mutationFn: async (careJourneyAttachmentId: string) =>
      carecoApi?.deleteCareJourneyAttachment(careJourneyAttachmentId, currentRoster!.id),
    onSuccess: (_, careJourneyAttachmentId) => {
      addAlert({
        type: ToastType.SUCCESS,
        content: 'Care journey attachment deleted successfully.',
      });
      optimisticDeleteCareJourneyAttachment(careJourneyAttachmentId);
      void queryClient.invalidateQueries({ queryKey: ['careJourneyDetails', careJourneyDetails.careJourneyInstanceId] });
    },
    onError: (error: AxiosError) => {
      Helpers.alertOnError(`Failed to delete care journey attachment. ${GENERICERROR} ${Helpers.traceId(error)}`, error);
    },
  });

  const optimisticDeleteCareJourneyAttachment = (careJourneyAttachmentId: string) => {
    queryClient.setQueryData<CareJourneyDetailsResponse>(
      ['careJourneyDetails', careJourneyDetails.careJourneyInstanceId],
      (careJourneyDetails) => {
        if (careJourneyDetails) {
          return {
            ...careJourneyDetails,
            attachments: careJourneyDetails.attachments
              .filter((attachment: CareJourneyAttachment) => attachment.id !== careJourneyAttachmentId),
          };
        }
      });
  };

  const handleDownload = async (careJourneyAttachmentId: string, filename: string) => {
    const response = await carecoApi?.getCareJourneyAttachment(careJourneyAttachmentId, currentRoster!.id);
    if (response) {
      const arrayBuffer = FileHelpers.base64ToArrayBuffer(response);
      const blob = new Blob([arrayBuffer]);
      FileHelpers.downloadFile(URL.createObjectURL(blob), filename);
    }
  };

  return (
    <>
      {canActOnTasks && (
        <ButtonWrapper>
          <Button onClick={() => setIsUploadModalOpen(true)}>
            <Icon iconClass={ICONS.plus} style={{ marginRight: sizes.xSmall }} />
            Upload attachment
          </Button>
        </ButtonWrapper>
      )}
      <TableWrapper>
        {!careJourneyDetails.attachments.length
          ? <EmptyTable>No attachments found</EmptyTable>
          : <Table
            header={['Attachment', 'Uploaded by', 'Date created', '']}
            stickyHeader
            data={careJourneyDetails.attachments
              .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
              .map((attachment) => {
                const filename = getFileName(attachment);
                const menuItems = canActOnTasks && attachment.type === CareJourneyAttachmentType.USER_UPLOAD
                  ? [{ label: 'Delete', action: () => setDeleteModalAttachmentId(attachment.id) }]
                  : [{ label: 'No actions available', action: () => {} }];

                return [
                  <AttachmentTitle>
                    <Button
                      styleType={StyleType.TERTIARY}
                      className={css({ marginLeft: '-1.4rem' })}
                      onClick={() => handleDownload(attachment.id, filename)}
                    >
                      {filename}
                    </Button>
                    <AttachmentStatus careJourneyAttachment={attachment} />
                  </AttachmentTitle>,
                  attachment.createdBy ? `${attachment.createdBy.first_name} ${attachment.createdBy.last_name}` : 'System',
                  DisplayHelpers.formatDateTime(attachment.createdAt),
                  <AttachmentActions>
                    <ContextMenu menuItems={menuItems}>
                      <IconButton
                        iconClass={ICONS['ellipsis-h']}
                        styleType={StyleType.TERTIARY}
                        disabled={attachment.id === NEW_RESOURCE}
                      />
                    </ContextMenu>
                  </AttachmentActions>,
                ];
              })}
          />
        }
      </TableWrapper>
      <UploadAttachmentModal
        isOpen={isUploadModalOpen}
        close={() => setIsUploadModalOpen(false)}
        careJourneyDetails={careJourneyDetails}
      />
      <DeleteModal
        title="Attachment"
        isOpen={!!deleteModalAttachmentId}
        close={() => setDeleteModalAttachmentId(null)}
        confirm={async () => handleDelete(deleteModalAttachmentId!)}
      />
    </>
  );
}
